import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Tab, Tabs, Table, Spinner, Button, Alert, Form, Card, Row, Col, Modal } from "react-bootstrap";
import {
  MdDelete,
  MdDownload,
  MdUpload,
  MdSearch,
  MdCheckCircle,
  MdError,
  MdWarning,
  MdDeleteForever,
  MdCalendarToday,
} from "react-icons/md";
import eliminarCheckInApi from "../../../services/api/eliminarCheckInApi";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";
import "../../../components/css/Dashboard.css";

const EliminarCheckIn = () => {
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));

  // Estados generales
  const [activeTab, setActiveTab] = useState("individual");

  // Estados para eliminación individual
  const [dniIndividual, setDniIndividual] = useState("");
  const [checkInEncontrado, setCheckInEncontrado] = useState(null);
  const [buscandoCheckIn, setBuscandoCheckIn] = useState(false);
  const [eliminandoIndividual, setEliminandoIndividual] = useState(false);
  const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);

  // Estados para eliminación masiva
  const [fileMasiva, setFileMasiva] = useState(null);
  const [validandoArchivo, setValidandoArchivo] = useState(false);
  const [resultadoValidacion, setResultadoValidacion] = useState(null);
  const [procesandoMasiva, setProcesandoMasiva] = useState(false);
  const [resultadosMasiva, setResultadosMasiva] = useState(null);
  const [autoCompleteDni, setAutoCompleteDni] = useState(true);

  // Estados para eliminar todos
  const [showModalEliminarTodos, setShowModalEliminarTodos] = useState(false);
  const [confirmacionTexto, setConfirmacionTexto] = useState("");
  const [eliminandoTodos, setEliminandoTodos] = useState(false);

  // Eliminar por fecha (pestaña Más)
  const [fechaPorFecha, setFechaPorFecha] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  });
  const [listaPorFecha, setListaPorFecha] = useState([]);
  const [cargandoPorFecha, setCargandoPorFecha] = useState(false);
  const [selectedPorFechaIds, setSelectedPorFechaIds] = useState([]);
  const [showModalEliminarPorFecha, setShowModalEliminarPorFecha] = useState(false);
  const [eliminandoPorFecha, setEliminandoPorFecha] = useState(false);
  const [modoEliminarPorFecha, setModoEliminarPorFecha] = useState("checkin_y_reserva");

  const showToast = (type, message) => {
    if (type === "success") {
      toast.success(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "error") {
      toast.error(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "info") {
      toast.info(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "warning") {
      toast.warning(message, { position: "bottom-right", autoClose: 3000 });
    }
  };

  // ============================================================================
  // ELIMINACIÓN INDIVIDUAL
  // ============================================================================

  const buscarCheckIn = async () => {
    if (!dniIndividual.trim()) {
      showToast("error", "Por favor ingresa un DNI");
      return;
    }

    setBuscandoCheckIn(true);
    setCheckInEncontrado(null);

    try {
      const response = await eliminarCheckInApi.buscarCheckIn(
        dniIndividual.trim(),
        selectedProject.id
      );

      if (response.data.success) {
        setCheckInEncontrado(response.data.data);
        showToast("success", "Check-in encontrado");
      } else {
        showToast("error", response.data.message || "Check-in no encontrado");
      }
    } catch (error) {
      console.error("Error al buscar check-in:", error);
      showToast("error", error.response?.data?.detail || "Error al buscar check-in");
    } finally {
      setBuscandoCheckIn(false);
    }
  };

  const confirmarEliminacion = () => {
    setShowModalConfirmacion(true);
  };

  const eliminarCheckInIndividual = async () => {
    setShowModalConfirmacion(false);
    setEliminandoIndividual(true);

    try {
      const response = await eliminarCheckInApi.eliminarCheckInIndividual(
        dniIndividual.trim(),
        selectedProject.id
      );

      if (response.data.success) {
        showToast("success", response.data.message || "Check-in eliminado exitosamente");
        setDniIndividual("");
        setCheckInEncontrado(null);
      } else {
        showToast("error", response.data.message || "Error al eliminar check-in");
      }
    } catch (error) {
      console.error("Error al eliminar check-in:", error);
      showToast("error", error.response?.data?.detail || "Error al eliminar check-in");
    } finally {
      setEliminandoIndividual(false);
    }
  };

  // ============================================================================
  // ELIMINACIÓN MASIVA
  // ============================================================================

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFileMasiva(selectedFile);
      setResultadoValidacion(null);
      setResultadosMasiva(null);
    }
  };

  const validarArchivo = async () => {
    if (!fileMasiva) {
      showToast("error", "Por favor selecciona un archivo");
      return;
    }

    setValidandoArchivo(true);
    try {
      const response = await eliminarCheckInApi.validarArchivo(fileMasiva, selectedProject.id);
      setResultadoValidacion(response.data);

      if (response.data.success) {
        showToast("success", `Archivo válido: ${response.data.filas_validas} filas listas para procesar`);
      } else {
        showToast("warning", `Archivo con errores: ${response.data.filas_con_errores} filas con problemas`);
      }
    } catch (error) {
      console.error("Error al validar archivo:", error);
      showToast("error", error.response?.data?.detail || "Error al validar archivo");
      setResultadoValidacion(null);
    } finally {
      setValidandoArchivo(false);
    }
  };

  const procesarEliminacionMasiva = async () => {
    if (!fileMasiva) {
      showToast("error", "Por favor selecciona un archivo");
      return;
    }

    if (!resultadoValidacion || !resultadoValidacion.success) {
      showToast("error", "Debes validar el archivo antes de procesarlo");
      return;
    }

    setProcesandoMasiva(true);
    try {
      const response = await eliminarCheckInApi.eliminarCheckInsMasivo(
        fileMasiva,
        autoCompleteDni,
        selectedProject.id
      );
      setResultadosMasiva(response.data);

      const exitosos = response.data.eliminaciones_exitosas || 0;
      const fallidos = response.data.eliminaciones_fallidas || 0;

      if (exitosos > 0 && fallidos === 0) {
        showToast("success", `✅ Todos los check-ins eliminados exitosamente (${exitosos} eliminados)`);
      } else if (exitosos > 0 && fallidos > 0) {
        showToast("warning", `⚠️ Eliminación parcial: ${exitosos} exitosos, ${fallidos} fallidos`);
      } else if (fallidos > 0) {
        showToast("error", `❌ Todos los check-ins fallaron (${fallidos} fallidos)`);
      }
    } catch (error) {
      console.error("Error al procesar eliminación masiva:", error);
      showToast("error", error.response?.data?.detail || "Error al procesar archivo");
      setResultadosMasiva(null);
    } finally {
      setProcesandoMasiva(false);
    }
  };

  const descargarPlantilla = () => {
    const plantilla = [
      { "Numero Documento Identidad": "12345678" },
      { "Numero Documento Identidad": "87654321" },
      { "Numero Documento Identidad": "11223344" },
    ];

    const ws = XLSX.utils.json_to_sheet(plantilla);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Eliminar Check-Ins");
    XLSX.writeFile(wb, "Plantilla_Eliminar_CheckIns.xlsx");
    showToast("info", "📄 Plantilla descargada");
  };

  const descargarErrores = () => {
    if (!resultadosMasiva || !resultadosMasiva.detalles_fallidos) {
      showToast("error", "No hay errores para descargar");
      return;
    }

    const errores = resultadosMasiva.detalles_fallidos.map((item) => ({
      Fila: item.fila,
      DNI: item.dni,
      Error: item.error,
    }));

    const ws = XLSX.utils.json_to_sheet(errores);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Errores");
    XLSX.writeFile(wb, "Errores_Eliminar_CheckIns.xlsx");
    showToast("info", "📄 Archivo de errores descargado");
  };

  // ============================================================================
  // ELIMINAR TODOS LOS CHECK-INS
  // ============================================================================

  const handleShowModalEliminarTodos = () => {
    setConfirmacionTexto("");
    setShowModalEliminarTodos(true);
  };

  const localIsoDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  };

  const cargarCheckInsPorFecha = async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!selectedProject?.id) {
      showToast("error", "Seleccione un proyecto");
      return;
    }
    if (!fechaPorFecha) {
      showToast("error", "Seleccione una fecha");
      return;
    }
    setCargandoPorFecha(true);
    setSelectedPorFechaIds([]);
    try {
      const response = await eliminarCheckInApi.listarCheckInsPorFecha(
        selectedProject.id,
        fechaPorFecha
      );
      if (response.data?.success) {
        setListaPorFecha(response.data.data || []);
        const n = response.data.count ?? (response.data.data || []).length;
        if (!silent) {
          showToast(
            n > 0 ? "success" : "info",
            n > 0 ? `${n} check-in(s) encontrados` : "Sin check-ins ese día"
          );
        }
      } else {
        setListaPorFecha([]);
        showToast("error", response.data?.message || "No se pudo cargar la lista");
      }
    } catch (error) {
      console.error(error);
      setListaPorFecha([]);
      showToast("error", error.response?.data?.detail || "Error al cargar check-ins");
    } finally {
      setCargandoPorFecha(false);
    }
  };

  const toggleSelectPorFecha = (asignacionId) => {
    setSelectedPorFechaIds((prev) =>
      prev.includes(asignacionId)
        ? prev.filter((id) => id !== asignacionId)
        : [...prev, asignacionId]
    );
  };

  const toggleSelectAllPorFecha = () => {
    const ids = listaPorFecha.map((r) => r.asignacion_id);
    const allOn =
      ids.length > 0 && ids.every((id) => selectedPorFechaIds.includes(id));
    setSelectedPorFechaIds(allOn ? [] : [...ids]);
  };

  const abrirModalEliminarPorFecha = (modo) => {
    if (selectedPorFechaIds.length === 0) {
      showToast("error", "Seleccione al menos un check-in");
      return;
    }
    setModoEliminarPorFecha(modo);
    setShowModalEliminarPorFecha(true);
  };

  const confirmarEliminarPorFecha = async () => {
    if (!selectedProject?.id) return;
    setEliminandoPorFecha(true);
    try {
      const response = await eliminarCheckInApi.eliminarCheckInsPorIds(
        selectedProject.id,
        selectedPorFechaIds,
        modoEliminarPorFecha
      );
      if (response.data?.success) {
        showToast("success", response.data.message || "Check-ins eliminados");
        setShowModalEliminarPorFecha(false);
        setSelectedPorFechaIds([]);
        await cargarCheckInsPorFecha({ silent: true });
      } else {
        showToast("error", response.data?.message || "Error al eliminar");
      }
    } catch (error) {
      console.error(error);
      showToast("error", error.response?.data?.detail || "Error al eliminar check-ins");
    } finally {
      setEliminandoPorFecha(false);
    }
  };

  const handleEliminarTodos = async () => {
    if (confirmacionTexto !== "CONFIRMAR") {
      showToast("error", "Debe escribir 'CONFIRMAR' para continuar");
      return;
    }

    setEliminandoTodos(true);
    try {
      const response = await eliminarCheckInApi.eliminarTodosCheckIns(selectedProject.id);

      if (response.data?.success) {
        const { eliminados } = response.data;
        showToast("success", `✅ Se eliminaron ${eliminados} check-ins exitosamente`);
        setShowModalEliminarTodos(false);
        setConfirmacionTexto("");
      } else {
        showToast("error", response.data?.message || "Error al eliminar check-ins");
      }
    } catch (error) {
      showToast("error", error.response?.data?.detail || "Error al eliminar todos los check-ins");
    } finally {
      setEliminandoTodos(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="dashboard-container">
      <ToastContainer />
      
      {/* Header con estilo Digital Harmony */}
      <div className="digital-harmony-header mb-4">
        <h1 className="digital-harmony-title">
          <MdDelete className="me-2" />
          Eliminar Check-In
        </h1>
        <p className="digital-harmony-subtitle">
          Elimina check-ins activos (estado ocupada) individual o masivamente
        </p>
      </div>

      <Card className="shadow-sm">
        <Card.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3 digital-harmony-tabs"
          >
            {/* ================================================================ */}
            {/* TAB 1: ELIMINACIÓN INDIVIDUAL */}
            {/* ================================================================ */}
            <Tab eventKey="individual" title="Eliminación Individual">
              <div className="p-3">
                {/* Búsqueda */}
                <Card className="mb-3 digital-harmony-card">
                  <Card.Header className="digital-harmony-card-header">
                    <MdSearch className="me-2" />
                    Buscar Check-In
                  </Card.Header>
                  <Card.Body>
                    <Row className="align-items-end">
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label>Número de Documento (DNI)</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Ej: 12345678"
                            value={dniIndividual}
                            onChange={(e) => setDniIndividual(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") buscarCheckIn();
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Button
                          variant="primary"
                          className="w-100 digital-harmony-btn"
                          onClick={buscarCheckIn}
                          disabled={buscandoCheckIn}
                        >
                          {buscandoCheckIn ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Buscando...
                            </>
                          ) : (
                            <>
                              <MdSearch className="me-2" />
                              Buscar
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Resultado de búsqueda */}
                {checkInEncontrado && (
                  <Card className="digital-harmony-card border-primary">
                    <Card.Header className="digital-harmony-card-header bg-primary text-white">
                      <MdCheckCircle className="me-2" />
                      Información del Check-In
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6} className="mb-3">
                          <strong>DNI:</strong>
                          <div>{checkInEncontrado.dni}</div>
                        </Col>
                        <Col md={6} className="mb-3">
                          <strong>Trabajador:</strong>
                          <div>{checkInEncontrado.trabajador}</div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <strong>Zona:</strong>
                          <div>{checkInEncontrado.zona}</div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <strong>Módulo:</strong>
                          <div>{checkInEncontrado.modulo}</div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <strong>Habitación:</strong>
                          <div>{checkInEncontrado.habitacion}</div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <strong>Cama:</strong>
                          <div>{checkInEncontrado.cama}</div>
                        </Col>
                        <Col md={6} className="mb-3">
                          <strong>Estado:</strong>
                          <div className="text-success fw-bold">
                            {checkInEncontrado.estado?.toUpperCase()}
                          </div>
                        </Col>
                        <Col md={6} className="mb-3">
                          <strong>Fecha Check-In:</strong>
                          <div>{checkInEncontrado.fecha_check_in || "N/A"}</div>
                        </Col>
                      </Row>
                      <hr />
                      <Button
                        variant="danger"
                        className="w-100"
                        onClick={confirmarEliminacion}
                        disabled={eliminandoIndividual}
                      >
                        <MdDelete className="me-2" />
                        Eliminar Check-In
                      </Button>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab>

            {/* ================================================================ */}
            {/* TAB 2: ELIMINACIÓN MASIVA */}
            {/* ================================================================ */}
            <Tab eventKey="masiva" title="Eliminación Masiva">
              <div className="p-3">
                {/* Instrucciones */}
                <Alert variant="info" className="digital-harmony-alert">
                  <strong>📋 Formato del archivo Excel:</strong>
                  <ul className="mb-0 mt-2">
                    <li>
                      <strong>Columna obligatoria:</strong> "Numero Documento Identidad"
                    </li>
                    <li>
                      Solo se eliminarán check-ins en estado <strong>"ocupada"</strong>
                    </li>
                    <li>La cama se liberará automáticamente (estado "libre")</li>
                    <li>Descarga la plantilla para ver el formato correcto</li>
                  </ul>
                </Alert>

                {/* Botones de acción */}
                <Card className="mb-3 digital-harmony-card">
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <Button
                          variant="outline-primary"
                          className="w-100"
                          onClick={descargarPlantilla}
                        >
                          <MdDownload className="me-2" />
                          Descargar Plantilla
                        </Button>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label
                            htmlFor="file-upload"
                            className="btn btn-outline-primary w-100 mb-0"
                          >
                            <MdUpload className="me-2" />
                            Seleccionar Archivo
                          </Form.Label>
                          <Form.Control
                            id="file-upload"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                          />
                        </Form.Group>
                      </Col>

                      {fileMasiva && (
                        <>
                          <Col xs={12}>
                            <Alert variant="success" className="mb-0">
                              <strong>Archivo seleccionado:</strong> {fileMasiva.name}
                            </Alert>
                          </Col>
                          <Col xs={12}>
                            <Form.Check
                              type="checkbox"
                              label="Autocompletar DNI con ceros a la izquierda"
                              checked={autoCompleteDni}
                              onChange={(e) => setAutoCompleteDni(e.target.checked)}
                            />
                          </Col>
                          <Col md={6}>
                            <Button
                              variant="info"
                              className="w-100"
                              onClick={validarArchivo}
                              disabled={validandoArchivo}
                            >
                              {validandoArchivo ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Validando...
                                </>
                              ) : (
                                <>
                                  <MdSearch className="me-2" />
                                  Validar Archivo
                                </>
                              )}
                            </Button>
                          </Col>
                          <Col md={6}>
                            <Button
                              variant="danger"
                              className="w-100"
                              onClick={procesarEliminacionMasiva}
                              disabled={
                                procesandoMasiva || !resultadoValidacion?.success
                              }
                            >
                              {procesandoMasiva ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Eliminando...
                                </>
                              ) : (
                                <>
                                  <MdDelete className="me-2" />
                                  Eliminar Check-Ins
                                </>
                              )}
                            </Button>
                          </Col>
                        </>
                      )}
                    </Row>
                  </Card.Body>
                </Card>

                {/* Resultado de validación */}
                {resultadoValidacion && (
                  <Card className="mb-3 digital-harmony-card">
                    <Card.Header className="digital-harmony-card-header">
                      📊 Resultado de Validación
                    </Card.Header>
                    <Card.Body>
                      <Row className="text-center">
                        <Col md={4}>
                          <Card className="mb-2">
                            <Card.Body>
                              <h5>Total Filas</h5>
                              <h3>{resultadoValidacion.total_filas || 0}</h3>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={4}>
                          <Card className="mb-2 bg-success bg-opacity-10">
                            <Card.Body>
                              <h5>Filas Válidas</h5>
                              <h3 className="text-success">
                                {resultadoValidacion.filas_validas || 0}
                              </h3>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={4}>
                          <Card className="mb-2 bg-danger bg-opacity-10">
                            <Card.Body>
                              <h5>Filas con Errores</h5>
                              <h3 className="text-danger">
                                {resultadoValidacion.filas_con_errores || 0}
                              </h3>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      {resultadoValidacion.errores &&
                        resultadoValidacion.errores.length > 0 && (
                          <div className="mt-3">
                            <Alert variant="warning">
                              Se encontraron {resultadoValidacion.errores.length}{" "}
                              errores:
                            </Alert>
                            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                              <Table striped bordered hover size="sm">
                                <thead>
                                  <tr>
                                    <th>Fila</th>
                                    <th>DNI</th>
                                    <th>Errores</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resultadoValidacion.errores.map((error, index) => (
                                    <tr key={index}>
                                      <td>{error.fila}</td>
                                      <td>{error.dni}</td>
                                      <td>{error.errores.join(", ")}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}
                    </Card.Body>
                  </Card>
                )}

                {/* Resultado de eliminación */}
                {resultadosMasiva && (
                  <Card className="digital-harmony-card">
                    <Card.Header className="digital-harmony-card-header">
                      ✅ Resultado de Eliminación
                    </Card.Header>
                    <Card.Body>
                      <Row className="text-center mb-3">
                        <Col md={6}>
                          <Card className="bg-success bg-opacity-10">
                            <Card.Body>
                              <MdCheckCircle
                                size={40}
                                className="text-success mb-2"
                              />
                              <h5>Eliminaciones Exitosas</h5>
                              <h2 className="text-success">
                                {resultadosMasiva.eliminaciones_exitosas || 0}
                              </h2>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card className="bg-danger bg-opacity-10">
                            <Card.Body>
                              <MdError size={40} className="text-danger mb-2" />
                              <h5>Eliminaciones Fallidas</h5>
                              <h2 className="text-danger">
                                {resultadosMasiva.eliminaciones_fallidas || 0}
                              </h2>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      {resultadosMasiva.detalles_fallidos &&
                        resultadosMasiva.detalles_fallidos.length > 0 && (
                          <div className="mt-3">
                            <Button
                              variant="outline-danger"
                              onClick={descargarErrores}
                              className="mb-2"
                            >
                              <MdDownload className="me-2" />
                              Descargar Errores
                            </Button>
                            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                              <Table striped bordered hover size="sm">
                                <thead>
                                  <tr>
                                    <th>Fila</th>
                                    <th>DNI</th>
                                    <th>Error</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resultadosMasiva.detalles_fallidos.map(
                                    (detalle, index) => (
                                      <tr key={index}>
                                        <td>{detalle.fila}</td>
                                        <td>{detalle.dni}</td>
                                        <td>{detalle.error}</td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                      {resultadosMasiva.detalles_exitosos &&
                        resultadosMasiva.detalles_exitosos.length > 0 && (
                          <div className="mt-3">
                            <Alert variant="success">
                              <strong>Check-ins eliminados exitosamente:</strong>
                            </Alert>
                            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                              <Table striped bordered hover size="sm">
                                <thead>
                                  <tr>
                                    <th>Fila</th>
                                    <th>DNI</th>
                                    <th>Trabajador</th>
                                    <th>Cama</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resultadosMasiva.detalles_exitosos.map(
                                    (detalle, index) => (
                                      <tr key={index}>
                                        <td>{detalle.fila}</td>
                                        <td>{detalle.dni}</td>
                                        <td>{detalle.trabajador}</td>
                                        <td>{detalle.cama}</td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab>

            {/* PESTAÑA: MÁS — Eliminar por fecha */}
            <Tab
              eventKey="mas"
              tabClassName="eliminar-checkin-tab-mas-item"
              contentClassName="eliminar-checkin-tab-mas-pane"
              title={
                <span className="d-flex flex-column align-items-start text-start lh-sm py-1 eliminar-checkin-tab-mas-title">
                  <span className="small opacity-75 fw-normal mb-1">Más</span>
                  <span className="d-flex align-items-center gap-1 fw-semibold">
                    <MdCalendarToday className="flex-shrink-0" size={18} />
                    <span>Eliminar por fecha</span>
                  </span>
                </span>
              }
            >
              <div className="pt-1">
                <Card className="mb-0 digital-harmony-card eliminar-por-fecha-card">
                  <Card.Header className="digital-harmony-card-header eliminar-por-fecha-section-header">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <MdCalendarToday size={22} className="flex-shrink-0" />
                      <span className="eliminar-por-fecha-heading">Eliminar por fecha</span>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="info" className="digital-harmony-alert mb-3">
                      Lista los check-ins <strong>activos (ocupada)</strong> cuya{" "}
                      <strong>fecha de check-in</strong> coincide con el día elegido (orden por hora).
                      Marque filas y elija: <strong>eliminar check-in y reserva</strong> (cancela la
                      asignación y libera la cama) o <strong>solo quitar check-in</strong> (borra la
                      fecha de check-in, deja la reserva activa y la cama en reservado).
                    </Alert>
                    <Row className="align-items-end g-3 mb-3">
                      <Col md={4} sm={6}>
                        <Form.Group>
                          <Form.Label>Fecha de check-in</Form.Label>
                          <Form.Control
                            type="date"
                            value={fechaPorFecha}
                            onChange={(e) => setFechaPorFecha(e.target.value)}
                            max={localIsoDate()}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4} sm={6}>
                        <Button
                          variant="primary"
                          className="w-100 digital-harmony-btn"
                          onClick={cargarCheckInsPorFecha}
                          disabled={cargandoPorFecha}
                        >
                          {cargandoPorFecha ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Cargando...
                            </>
                          ) : (
                            <>
                              <MdSearch className="me-2" />
                              Buscar
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>

                    {listaPorFecha.length > 0 && (
                      <>
                        <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={toggleSelectAllPorFecha}
                          >
                            {listaPorFecha.length > 0 &&
                            listaPorFecha.every((r) =>
                              selectedPorFechaIds.includes(r.asignacion_id)
                            )
                              ? "Desmarcar todos"
                              : "Seleccionar todos"}
                          </Button>
                          <span className="text-muted small">
                            {selectedPorFechaIds.length} seleccionado(s)
                          </span>
                          <div className="d-flex flex-wrap gap-2 ms-auto">
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => abrirModalEliminarPorFecha("solo_checkin")}
                              disabled={selectedPorFechaIds.length === 0}
                              title="Mantiene la reserva: limpia fecha de check-in y estado reservada"
                            >
                              <MdDelete className="me-1" />
                              Solo quitar check-in
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => abrirModalEliminarPorFecha("checkin_y_reserva")}
                              disabled={selectedPorFechaIds.length === 0}
                              title="Cancela asignación y libera la cama"
                            >
                              <MdDeleteForever className="me-1" />
                              Check-in y reserva
                            </Button>
                          </div>
                        </div>
                        <div style={{ maxHeight: "420px", overflowY: "auto" }}>
                          <Table striped bordered hover size="sm" responsive>
                            <thead className="table-light sticky-top">
                              <tr>
                                <th style={{ width: "42px" }}>
                                  <Form.Check
                                    type="checkbox"
                                    aria-label="Seleccionar todos"
                                    checked={
                                      listaPorFecha.length > 0 &&
                                      listaPorFecha.every((r) =>
                                        selectedPorFechaIds.includes(r.asignacion_id)
                                      )
                                    }
                                    ref={(el) => {
                                      if (el) {
                                        el.indeterminate =
                                          selectedPorFechaIds.length > 0 &&
                                          !listaPorFecha.every((r) =>
                                            selectedPorFechaIds.includes(r.asignacion_id)
                                          );
                                      }
                                    }}
                                    onChange={toggleSelectAllPorFecha}
                                  />
                                </th>
                                <th>Hora</th>
                                <th>DNI</th>
                                <th>Trabajador</th>
                                <th>Zona</th>
                                <th>Módulo</th>
                                <th>Hab.</th>
                                <th>Cama</th>
                              </tr>
                            </thead>
                            <tbody>
                              {listaPorFecha.map((row) => (
                                <tr key={row.asignacion_id}>
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      checked={selectedPorFechaIds.includes(row.asignacion_id)}
                                      onChange={() => toggleSelectPorFecha(row.asignacion_id)}
                                      aria-label={`Seleccionar ${row.dni}`}
                                    />
                                  </td>
                                  <td className="text-nowrap fw-semibold">
                                    {row.hora_check_in || "—"}
                                  </td>
                                  <td>{row.dni}</td>
                                  <td>{row.trabajador}</td>
                                  <td>{row.zona}</td>
                                  <td>{row.modulo}</td>
                                  <td>{row.habitacion}</td>
                                  <td>{row.cama}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}

                    {!cargandoPorFecha && listaPorFecha.length === 0 && fechaPorFecha && (
                      <p className="text-muted mb-0 small">
                        Pulse <strong>Buscar</strong> para cargar los check-ins del día (si no hay
                        resultados, la tabla no se muestra).
                      </p>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </Tab>

            {/* PESTAÑA: ELIMINAR TODOS */}
            <Tab eventKey="todos" title="Eliminar Todos">
              <div className="mt-4">
                <h5 className="mb-3 text-danger">⚠️ Eliminar Todos los Check-Ins Activos</h5>
                <Alert variant="danger">
                  <MdWarning className="me-2" size={24} />
                  <strong>PRECAUCIÓN:</strong> Esta acción eliminará TODOS los check-ins en estado <strong>'ocupada'</strong> del proyecto actual.
                  Los check-ins con check-out realizado (finalizados) NO serán afectados.
                </Alert>

                <p className="text-muted">
                  Esta opción está diseñada para limpiar todos los check-ins activos del proyecto de una sola vez.
                </p>

                <Button
                  variant="danger"
                  onClick={handleShowModalEliminarTodos}
                  size="lg"
                  className="dh-btn-danger"
                >
                  <MdDeleteForever className="me-2" />
                  Eliminar Todos los Check-Ins
                </Button>
              </div>
            </Tab>

          </Tabs>
        </Card.Body>
      </Card>

      {/* Modal de Confirmación de Eliminación */}
      <Modal
        show={showModalConfirmacion}
        onHide={() => setShowModalConfirmacion(false)}
        centered
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <MdDelete className="me-2" />
            Confirmar Eliminación de Check-In
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {checkInEncontrado && (
            <>
              <Alert variant="warning" className="mb-3">
                <strong>⚠️ Advertencia:</strong> Esta acción no se puede deshacer
              </Alert>
              
              <p className="mb-3">
                ¿Estás seguro de eliminar el check-in del siguiente trabajador?
              </p>

              <Card className="mb-3">
                <Card.Body>
                  <Row>
                    <Col md={12} className="mb-2">
                      <strong>Trabajador:</strong> {checkInEncontrado.trabajador}
                    </Col>
                    <Col md={12} className="mb-2">
                      <strong>DNI:</strong> {checkInEncontrado.dni}
                    </Col>
                    <Col md={12} className="mb-2">
                      <strong>Ubicación:</strong>{" "}
                      {checkInEncontrado.zona} - {checkInEncontrado.modulo} -{" "}
                      {checkInEncontrado.habitacion} - {checkInEncontrado.cama}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Alert variant="info" className="mb-0">
                <strong>Esta acción:</strong>
                <ul className="mb-0 mt-2">
                  <li>Cancelará la asignación de cama</li>
                  <li>Liberará la cama (estado: libre)</li>
                  <li>Registrará la eliminación en el sistema</li>
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModalConfirmacion(false)}
            disabled={eliminandoIndividual}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={eliminarCheckInIndividual}
            disabled={eliminandoIndividual}
          >
            {eliminandoIndividual ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Eliminando...
              </>
            ) : (
              <>
                <MdDelete className="me-2" />
                Sí, Eliminar Check-In
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal eliminar por fecha (selección múltiple) */}
      <Modal
        show={showModalEliminarPorFecha}
        onHide={() => !eliminandoPorFecha && setShowModalEliminarPorFecha(false)}
        centered
      >
        <Modal.Header
          closeButton
          className={
            modoEliminarPorFecha === "solo_checkin"
              ? "bg-warning"
              : "bg-danger text-white"
          }
        >
          <Modal.Title>
            {modoEliminarPorFecha === "solo_checkin" ? (
              <>
                <MdDelete className="me-2" />
                Quitar solo check-in
              </>
            ) : (
              <>
                <MdDeleteForever className="me-2" />
                Eliminar check-in y reserva
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modoEliminarPorFecha === "solo_checkin" ? (
            <Alert variant="warning" className="mb-0">
              En <strong>{selectedPorFechaIds.length}</strong> registro(s) se borrará la{" "}
              <strong>fecha de check-in</strong>, el estado de la asignación pasará a{" "}
              <strong>reservada</strong> y la cama volverá a <strong>reservado</strong>. La reserva
              sigue activa.
            </Alert>
          ) : (
            <Alert variant="danger" className="mb-0">
              Se cancelarán <strong>{selectedPorFechaIds.length}</strong> asignación(es): check-in y
              reserva. Las camas quedarán <strong>libres</strong>. Esta acción no se puede deshacer.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModalEliminarPorFecha(false)}
            disabled={eliminandoPorFecha}
          >
            Cancelar
          </Button>
          <Button
            variant={modoEliminarPorFecha === "solo_checkin" ? "warning" : "danger"}
            onClick={confirmarEliminarPorFecha}
            disabled={eliminandoPorFecha}
          >
            {eliminandoPorFecha ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Procesando...
              </>
            ) : modoEliminarPorFecha === "solo_checkin" ? (
              <>
                <MdDelete className="me-2" />
                Sí, quitar check-in
              </>
            ) : (
              <>
                <MdDeleteForever className="me-2" />
                Sí, eliminar todo
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Confirmar Eliminar Todos */}
      <Modal
        show={showModalEliminarTodos}
        onHide={() => setShowModalEliminarTodos(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <MdWarning className="me-2" />
            Confirmar Eliminación Total
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <p className="mb-0">
              <strong>Esta acción es irreversible.</strong> Se eliminarán TODOS los check-ins en estado 'ocupada' del proyecto <strong>{selectedProject?.nombre}</strong>.
            </p>
          </Alert>

          <Alert variant="info" className="mb-3">
            <strong>Esta acción NO afectará:</strong>
            <ul className="mb-0 mt-2">
              <li>Check-ins con check-out realizado (finalizados)</li>
              <li>Reservas pendientes (sin check-in)</li>
            </ul>
          </Alert>

          <p className="mb-3">
            Para confirmar esta acción, escriba <strong>CONFIRMAR</strong> en el campo de abajo:
          </p>

          <Form.Control
            type="text"
            placeholder="Escriba CONFIRMAR"
            value={confirmacionTexto}
            onChange={(e) => setConfirmacionTexto(e.target.value)}
            disabled={eliminandoTodos}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModalEliminarTodos(false)}
            disabled={eliminandoTodos}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleEliminarTodos}
            disabled={eliminandoTodos || confirmacionTexto !== "CONFIRMAR"}
          >
            {eliminandoTodos ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Eliminando...
              </>
            ) : (
              <>
                <MdDeleteForever className="me-2" />
                Eliminar Todos
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .digital-harmony-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 2rem;
          border-radius: 10px;
          color: white;
        }
        .digital-harmony-title {
          font-size: 2rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        .digital-harmony-subtitle {
          font-size: 1.1rem;
          margin-bottom: 0;
          opacity: 0.95;
        }
        .digital-harmony-card {
          border-radius: 10px;
          border: 1px solid #e0e0e0;
        }
        .digital-harmony-card-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-weight: 600;
          border-radius: 10px 10px 0 0 !important;
        }
        .digital-harmony-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
        }
        .digital-harmony-btn:hover {
          background: linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%);
        }
        .digital-harmony-tabs .nav-link {
          font-weight: 600;
          font-size: 1rem;
        }
        .digital-harmony-tabs .nav-link.active {
          color: #667eea;
          border-bottom: 3px solid #667eea;
        }
        .digital-harmony-alert {
          border-radius: 10px;
        }
        /* Pestaña Más: más aire para el título y el contenido */
        :global(.eliminar-checkin-tab-mas-item .nav-link) {
          padding: 0.85rem 1.35rem !important;
          min-height: 4.25rem;
          align-items: flex-start !important;
        }
        :global(.eliminar-checkin-tab-mas-pane) {
          padding: 1.75rem 1.5rem 2.25rem !important;
        }
        @media (min-width: 768px) {
          :global(.eliminar-checkin-tab-mas-pane) {
            padding: 2rem 2.25rem 2.5rem !important;
          }
        }
        .eliminar-por-fecha-section-header {
          padding: 1.15rem 1.5rem !important;
        }
        .eliminar-por-fecha-heading {
          font-size: 1.25rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          line-height: 1.35;
        }
        .eliminar-por-fecha-card :global(.card-body) {
          padding: 1.35rem 1.5rem 1.5rem;
        }
        @media (min-width: 768px) {
          .eliminar-por-fecha-card :global(.card-body) {
            padding: 1.5rem 1.75rem 1.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default EliminarCheckIn;
